import React, { useRef, useState, useEffect, useMemo } from "react";
import { Modal, Box, Button, IconButton, Tooltip, Checkbox } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import TocIcon from "@mui/icons-material/Toc";
import PrintIcon from "@mui/icons-material/Print";
import * as XLSX from "xlsx-js-style";
import { useTranslation } from "react-i18next";
import { beautifyNumber } from "../../../Utils/FormatNumber";
import PythonService from "../../../Services/pythonScripts.service";
import EnumsService from "../../../Services/enums.service";
import { useHamburgerMenu } from "../../../Contexts/HamburgerMenuContext";
import { toast } from "react-toastify";
import hebrewFontBase64 from "./NotoSansHebrew-Regular.base64.txt";
import './AccountCardReportHTML.css';
import { getCaseNumberLabel } from '../../../Utils/ReportUtils';
import { applyWorkbookRtl, AMOUNT_NUMBER_FORMAT } from '../../../Utils/ExcelReport';
import { styled } from "@mui/material/styles";
import ChatIcon from '@mui/icons-material/Chat';
import TableChartIcon from '@mui/icons-material/TableChart';
import Constants from '../../../assests/Constants/constants.js';
import SignatureModal from '../../Widgets/SignatureModal/SignatureModal';
import ReportLanguageSelector from '../../LedgerReport/ReportLanguageSelector/ReportLanguageSelector';
import { useReportTranslation } from '../../../hooks/useReportTranslation';
import Swal from 'sweetalert2';
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "70%",
  height: "90%",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: "10px",
  overflowY: "auto",
};

// Sleek styled button component
const SleekButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  borderRadius: '8px',
  padding: '8px 16px',
  fontSize: '12px',
  fontWeight: '600',
  textTransform: 'none',
  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
  border: 'none',
  color: '#ffffff',
  minWidth: 'auto',
  height: '36px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
  },
  '&:active': {
    transform: 'translateY(0px)',
    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
  },
  '& .MuiButton-startIcon': {
    marginRight: '6px',
    marginLeft: '0px',
    '& svg': {
      fontSize: '16px',
    }
  }
}));

// Sleek icon button component
const SleekIconButton = styled(IconButton)(({ theme }) => ({
  background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
  borderRadius: '8px',
  width: '36px',
  height: '36px',
  boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
  color: '#ffffff',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: 'linear-gradient(135deg, #45a049 0%, #3d8b40 100%)',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(76, 175, 80, 0.4)',
  },
  '&:active': {
    transform: 'translateY(0px)',
    boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
  },
  '& svg': {
    fontSize: '18px',
  }
}));

const MAX_DESC_LENGTH = 40;

/** Upload/document column headers: keep &nbsp; in embedded HTML; use <br> for PDF generation only. */
const htmlForAccountCardPdf = (html) =>
  html
    .replace(/תאריך&nbsp;רישום/g, 'תאריך<br>רישום')
    .replace(/תאריך&nbsp;מסמך/g, 'תאריך<br>מסמך');

const truncateDesc = (text) => {
  if (!text) return '';
  return text.length > MAX_DESC_LENGTH
    ? text.slice(0, MAX_DESC_LENGTH) + '...'
    : text;
};

const sortBy = (arr, key, type, dir) => {
  if (!key) return arr;
  const mult = dir === "desc" ? -1 : 1;
  return [...arr].sort((a, b) => {
    let v1 = a[key],
      v2 = b[key];
    if (type === "date") {
      v1 = new Date(v1);
      v2 = new Date(v2);
    }
    if (type === "number") {
      v1 = parseFloat(v1 ?? 0);
      v2 = parseFloat(v2 ?? 0);
    }
    if (v1 < v2) return -1 * mult;
    if (v1 > v2) return 1 * mult;
    return 0;
  });
};

const AccountCardReportHTML = ({
  open,
  handleClose,
  data,
  onSortConfigChange,
  token,
  initialData,
  finalBalance = 0,
  palCodeCategoryOptions = [],
  userTypeContext,
  selectedCardsForReport = [],
  selectedCurrency = null
}) => {
  const reportContentRef = useRef(null);
  const [hebrewFont, setHebrewFont] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "",
    dir: "asc",
    type: "",
  });
  const { i18n, t } = useTranslation();
  const direction = i18n.dir();

  // Signature state
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showAccountCodeId, setShowAccountCodeId] = useState(true);

  // Chat functionality
  const { handleSendMessageToChat, selectUserAndOpenChat } = useHamburgerMenu();
  /* ------------ LOAD FONT ------------ */
  useEffect(() => {
    fetch(hebrewFontBase64)
      .then((r) => r.text())
      .then(setHebrewFont)
      .catch(console.error);
  }, []);

  /* ------------ LISTEN FOR HEADER CLICKS FROM THE HTML ------------ */
  useEffect(() => {
    const handler = (e) => {
      const { key, type } = JSON.parse(e.detail);
      setSortConfig((prev) => {
        const dir =
          prev.key === key && prev.dir === "asc" ? "desc" : "asc";
        const cfg = { key, dir, type };
        onSortConfigChange?.(cfg);
        return cfg;
      });
    };
    window.addEventListener("headerSortUpdate", handler);
    return () => window.removeEventListener("headerSortUpdate", handler);
  }, [onSortConfigChange]);


  const formatMonthYear = d =>
    new Date(d).toLocaleDateString("he-IL", { month: "2-digit", year: "numeric" });

  const formatReportDate = d =>
    data?.filterByDocumentDate
      ? new Date(d).toLocaleDateString("he-IL", { day: "2-digit", month: "2-digit", year: "numeric" })
      : formatMonthYear(d);

  /* ------------ BUILD HTML ------------ */
  const reportHtmlContent = useMemo(() => {
    if (!data) return "";

    /* ---- Foreign-currency (dual columns) setup ---- */
    const showFc = selectedCurrency !== null && selectedCurrency !== undefined;
    const fcOnly = typeof selectedCurrency === 'number';
    // numeric enum value -> code name (e.g. 0 -> "USD")
    const fcCodeName = Object.entries(Constants.ForeignCurrencyCode || {})
      .find(([, value]) => value === selectedCurrency)?.[0];
    const fcSymbol = fcCodeName ? (Constants.ForeignCurrencySymbol?.[fcCodeName] || fcCodeName) : '';
    const fcLabel = fcSymbol ? `מט"ח (${fcSymbol})` : 'מט"ח';
    const colCount = fcOnly ? 10 : (showFc ? 13 : 10);
    const useLandscape = showFc;
    const fcNum = (v) => (v === null || v === undefined ? '' : beautifyNumber(v));
    const fcColGroup = showFc && !fcOnly ? `<col class="c-debit" /><col class="c-credit" /><col class="c-balance" />` : '';
    const amountColGroup = fcOnly
      ? `<col class="c-debit" /><col class="c-credit" /><col class="c-balance" />`
      : `<col class="c-debit" /><col class="c-credit" /><col class="c-balance" />${fcColGroup}`;

    const foldFcOpening = (fcDebit, fcCredit, fcBalance) => {
      const d = Number(fcDebit) || 0;
      const c = Number(fcCredit) || 0;
      const b = Number(fcBalance) || 0;
      if (b === 0 && d === 0 && c === 0) return { debit: d, credit: c, balance: b };
      const opening = b - d + c;
      const openingDebit = opening > 0 ? opening : 0;
      const openingCredit = opening < 0 ? -opening : 0;
      return { debit: d + openingDebit, credit: c + openingCredit, balance: b };
    };

    const fcRowHasActivity = (tr) =>
      (Number(tr?.foreignCurrencyDebit) || 0) !== 0 ||
      (Number(tr?.foreignCurrencyCredit) || 0) !== 0 ||
      (Number(tr?.foreignCurrencyBalance) || 0) !== 0;

    const renderAmountHeaderCells = () => {
      if (fcOnly) {
        return `
        ${headerCell(`חובה&nbsp;${fcLabel}`, "foreignCurrencyDebit", "number")}
        ${headerCell(`זכות&nbsp;${fcLabel}`, "foreignCurrencyCredit", "number")}
        ${headerCell(`יתרה&nbsp;${fcLabel}`, "foreignCurrencyBalance", "number")}`;
      }
      return `
        ${headerCell("חובה", "debitAccount", "number")}
        ${headerCell("זכות", "creditAccount", "number")}
        ${headerCell("יתרה", "balance", "number")}
        ${showFc ? headerCell(`חובה&nbsp;${fcLabel}`, "foreignCurrencyDebit", "number") : ''}
        ${showFc ? headerCell(`זכות&nbsp;${fcLabel}`, "foreignCurrencyCredit", "number") : ''}
        ${showFc ? headerCell(`יתרה&nbsp;${fcLabel}`, "foreignCurrencyBalance", "number") : ''}`;
    };

    const renderTransactionAmountCells = (tr) => {
      if (fcOnly) {
        const folded = foldFcOpening(tr.foreignCurrencyDebit, tr.foreignCurrencyCredit, tr.foreignCurrencyBalance);
        return `
        <td class="num">${fcNum(folded.debit)}</td>
        <td class="num">${fcNum(folded.credit)}</td>
        <td class="num">${fcNum(folded.balance)}</td>`;
      }
      return `
        <td>${beautifyNumber(tr.debitAccount)}</td>
        <td>${beautifyNumber(tr.creditAccount)}</td>
        <td>${beautifyNumber(tr.balance)}</td>
        ${showFc ? `<td>${fcNum(tr.foreignCurrencyDebit)}</td>` : ''}
        ${showFc ? `<td>${fcNum(tr.foreignCurrencyCredit)}</td>` : ''}
        ${showFc ? `<td>${fcNum(tr.foreignCurrencyBalance)}</td>` : ''}`;
    };

    const renderOpeningAmountCells = (debit, credit, balance, fcBalance) => {
      if (fcOnly) {
        const fcDebit = fcBalance > 0 ? fcBalance : 0;
        const fcCredit = fcBalance < 0 ? Math.abs(fcBalance) : 0;
        return `
                  <td class="num">${fcNum(fcDebit)}</td>
                  <td class="num">${fcNum(fcCredit)}</td>
                  <td class="num">${fcNum(fcBalance)}</td>`;
      }
      return `
                  <td class="num">${beautifyNumber(debit)}</td>
                  <td class="num">${beautifyNumber(credit)}</td>
                  <td class="num">${beautifyNumber(balance)}</td>
                  ${showFc ? `<td class="num">${fcNum(fcBalance)}</td>` : ''}
                  ${showFc ? `<td class="center muted"></td>` : ''}
                  ${showFc ? `<td class="num">${fcNum(fcBalance)}</td>` : ''}`;
    };

    const renderTotalsAmountCells = (debit, credit, balance, fcDebit, fcCredit, fcBal, underlineClass = 'triple-underline') => {
      if (fcOnly) {
        return `
                <td class="td-last-sum">${beautifyNumber(fcDebit)}<div class="${underlineClass}"></div></td>
                <td class="td-last-sum">${beautifyNumber(fcCredit)}<div class="${underlineClass}"></div></td>
                <td class="td-last-sum">${beautifyNumber(fcBal)}<div class="${underlineClass}"></div></td>`;
      }
      return `
                <td class="td-last-sum">
                  ${beautifyNumber(debit)}
                  <div class="${underlineClass}"></div>
                </td>
                <td class="td-last-sum">
                  ${beautifyNumber(credit)}
                  <div class="${underlineClass}"></div>
                </td>
                <td class="td-last-sum">
                  ${beautifyNumber(balance)}
                  <div class="${underlineClass}"></div>
                </td>
                ${showFc ? `<td class="td-last-sum">${beautifyNumber(fcDebit)}<div class="${underlineClass}"></div></td>
                <td class="td-last-sum">${beautifyNumber(fcCredit)}<div class="${underlineClass}"></div></td>
                <td class="td-last-sum">${beautifyNumber(fcBal)}<div class="${underlineClass}"></div></td>` : ''}`;
    };

    /* sort */
    const sorted = sortBy(
      data.transactions,
      sortConfig.key,
      sortConfig.type,
      sortConfig.dir
    );

    const derivedInitialDebit = initialData?.balance >= 0 ? initialData.balance : 0;
    const derivedInitialCredit = initialData?.balance < 0 ? Math.abs(initialData.balance) : 0;


    // Total debit/credit should NOT include opening balance (only transactions)
    const totalDebitAmount = data.transactions.reduce(
      (acc, cur) => acc + (cur.debitAccount || 0),
      0
    );

    const totalCreditAmount = data.transactions.reduce(
      (acc, cur) => acc + (cur.creditAccount || 0),
      0
    );

    // Final balance SHOULD include opening balance
    const finalBal = (totalDebitAmount + derivedInitialDebit) - (totalCreditAmount + derivedInitialCredit);

    /* ==== REPEATING HEADER COVER ================================= */

    // Extract unique card IDs from account cards array or transactions
    // Filter by selected cards if available
    let uniqueCardIds = [];
    let filteredAccountCards = data.accountCards && Array.isArray(data.accountCards) ? data.accountCards : [];

    // If there are selected cards, filter to only show selected ones
    if (selectedCardsForReport && selectedCardsForReport.length > 0 && filteredAccountCards.length > 0) {
      const selectedCardIds = selectedCardsForReport.map(card => 
        card.accountCardId || card.cardId
      ).filter(Boolean);
      
      filteredAccountCards = filteredAccountCards.filter(ac => 
        selectedCardIds.includes(ac.accountCardId)
      );
    }

    if (filteredAccountCards.length > 0) {
      // Use filtered account cards array for new format
      uniqueCardIds = filteredAccountCards
        .map(card => card.accountCardId)
        .filter(Boolean)
        .sort((a, b) => parseInt(a) - parseInt(b));
    } else {
      // Fallback to old format - extract from transactions and data object
      const cardIdsFromTransactions = [...new Set(data.transactions?.map(t => t.cardId).filter(Boolean))];
      const cardIdsFromData = data.accountCardId ? [data.accountCardId] : [];
      uniqueCardIds = [...new Set([...cardIdsFromData, ...cardIdsFromTransactions])].sort((a, b) => parseInt(a) - parseInt(b));
    }
    // Get PAL code categories / section numbers (orderNumber) for the header "מסעיף"
    let uniquePalCodeCategories = [];
    let palCodeSections = [];

    const resolveSectionNumber = (value) => {
      if (value == null || value === '' || value === '-' || value === 'N/A') return null;

      // Match category by id
      let cat = palCodeCategoryOptions.find((c) => c.palCodeCategoryId === value);
      if (cat?.orderNumber != null && cat.orderNumber !== '') return cat.orderNumber;

      // Match category by accounting group / other name fields
      cat = palCodeCategoryOptions.find(
        (c) =>
          c.accountingGroup === value ||
          c.palCodeCategory === value ||
          c.name === value
      );
      if (cat?.orderNumber != null && cat.orderNumber !== '') return cat.orderNumber;

      // Value itself may already be a numeric section
      const asNum = Number(value);
      if (!Number.isNaN(asNum) && String(value).trim() !== '') return asNum;

      return null;
    };

    const sectionsFromCards = (cards) => {
      const sections = [];
      (cards || []).forEach((card) => {
        // 1) Direct category on the card
        let section = resolveSectionNumber(card?.palCodeCategory);
        if (section != null) {
          sections.push(section);
          return;
        }

        // 2) Match via top-level palCode / palCodeCategory arrays (same index)
        const pal = card?.palCode;
        if (pal && data.palCode && Array.isArray(data.palCode)) {
          const idx = data.palCode.findIndex((p) => p === pal);
          if (idx !== -1 && data.palCodeCategory) {
            const catVal = Array.isArray(data.palCodeCategory)
              ? data.palCodeCategory[idx]
              : data.palCodeCategory;
            section = resolveSectionNumber(catVal);
            if (section != null) {
              sections.push(section);
              return;
            }
          }
        }

        // 3) Last resort: treat card.palCode as a category id (some datasets store it that way)
        section = resolveSectionNumber(pal);
        if (section != null) sections.push(section);
      });
      return [...new Set(sections)].sort((a, b) => a - b);
    };

    // Prefer sections from the cards actually shown (critical for single-card selection)
    palCodeSections = sectionsFromCards(filteredAccountCards);

    // Fallback: top-level / transaction categories (legacy / multi-card report data)
    if (palCodeSections.length === 0) {
      if (data.palCodeCategory && Array.isArray(data.palCodeCategory)) {
        uniquePalCodeCategories = data.palCodeCategory;
      } else {
        const palCodeCategoriesFromData = data.palCodeCategory ? [data.palCodeCategory] : [];
        const palCodeCategoriesFromTransactions = [
          ...new Set(data.transactions?.map((t) => t.palCodeCategory).filter(Boolean)),
        ];
        uniquePalCodeCategories = [
          ...new Set([...palCodeCategoriesFromData, ...palCodeCategoriesFromTransactions]),
        ];
      }

      palCodeSections = [
        ...new Set(
          uniquePalCodeCategories.map((palCodeCategory) => resolveSectionNumber(palCodeCategory)).filter((v) => v != null)
        ),
      ].sort((a, b) => a - b);
    }

    // Format card range - use the actual card IDs from the data
    const formatCardRange = () => {
      // Prefer filtered/selected cards so single-card report shows that card id
      if (uniqueCardIds.length === 1) return uniqueCardIds[0];
      if (uniqueCardIds.length > 1) {
        return `${uniqueCardIds[0]} עד כרטיס ${uniqueCardIds[uniqueCardIds.length - 1]}`;
      }

      // Use the accountCardId from data if available and it's not a comma-separated list
      if (data.accountCardId && data.accountCardId !== 'N/A' && data.accountCardId !== 'All Cards' && !String(data.accountCardId).includes(',')) {
        return data.accountCardId;
      }

      return '';
    };

    // Format PAL code range - show range from first to last section
    const formatPalCodeRange = () => {
      if (palCodeSections.length === 0) return '';
      if (palCodeSections.length === 1) return palCodeSections[0];
      // Show range from first to last section
      return `${palCodeSections[0]} עד ${palCodeSections[palCodeSections.length - 1]}`;
    };

    // Create main header (shown only once)
    // Create main header (screen + print variants)
    const createMainHeader = () => {
      const now = new Date();
      const currentTime = now.toLocaleTimeString("he-IL", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false
      });
      // Format date as DD/MM/YY instead of DD.MM.YY
      const day = now.getDate().toString().padStart(2, '0');
      const month = (now.getMonth() + 1).toString().padStart(2, '0');
      const year = now.getFullYear().toString().slice(-2);
      const currentDate = `${day}/${month}/${year}`;

      const cardRange = formatCardRange();
      const palCodeRange = formatPalCodeRange();
      const hasCardRange = Boolean(cardRange);
      const hasPalCodeRange = Boolean(palCodeRange);
      const dateRangeText = `מתאריך ${formatReportDate(data.startDate)} עד תאריך ${formatReportDate(data.EndDate)}`;

      // Pair each label with its date in equal columns.
      // RTL order: first col = right, second col = left → מסעיף right, מכרטיס left
      const metaCols = [];
      if (hasPalCodeRange) {
        metaCols.push(`
      <div class="header-meta-col-left">
        <span class="u header-meta-label">מסעיף ${palCodeRange}</span>
        <span class="u header-meta-date">${dateRangeText}</span>
      </div>`);
      }
      if (hasCardRange) {
        metaCols.push(`
      <div class="header-meta-col-right">
        <span class="u header-meta-label">מכרטיס ${cardRange}</span>
        <span class="u header-meta-date">${dateRangeText}</span>
      </div>`);
      }

      const rangeLineHtml = metaCols.length > 0
        ? `<div class="header-meta-grid${metaCols.length === 1 ? ' single-col' : ''}">${metaCols.join('')}</div>`
        : '';

      return `
        <div id="header">
          <div class="upper-name-div" >
            <div class="right-header-info">
              <p class="first" style="font-size: 12px; ">${getCaseNumberLabel(data?.caseType)}: ${data.clientCase}</p>
              <p class="first" style="font-size: 12px; ">${data.clientName}</p>
            </div>
              <div class="left-header-info">
              <div class="current-datetime">${currentDate} ${currentTime}</div>
            </div>
          </div>
        </div>
        <div class="header-wrapper">

          <div class="report-title-container">

            <h2 class="report-title">דוח כרטסת הנהלת חשבונות${showFc ? ` - ${fcLabel}` : ''}</h2>
            <div class="spacer"></div>
          </div>

          ${rangeLineHtml}
        </div>
      `;
    };



    // Helper function to get accountingGroupClassifaction for a specific combination

    // Helper function to get palCodeType for a specific combination
    const getPalCodeType = (palCode, accountCard) => {
      // If we have accountCards array, try to find the specific one
      if (data.accountCards && Array.isArray(data.accountCards)) {
        // First try exact match by PAL code and account card name
        let matchingAccountCard = data.accountCards.find(ac =>
          ac.palCode === palCode && ac.accountCardName === accountCard
        );


        // If no exact match, try by PAL code only (for cases where account card names might differ)
        if (!matchingAccountCard) {
          matchingAccountCard = data.accountCards.find(ac =>
            ac.palCode === palCode
          );
        }

        // If still no match, try by account card name only (for cases where PAL codes might be empty)
        if (!matchingAccountCard) {
          matchingAccountCard = data.accountCards.find(ac =>
            ac.accountCardName === accountCard
          );
        }

        if (matchingAccountCard && matchingAccountCard.palCodeType) {
          return matchingAccountCard.palCodeType;
        }
      }

      // Fallback: try to match by PAL code index in the arrays
      if (data.palCode && Array.isArray(data.palCode) && data.palCodeType && Array.isArray(data.palCodeType)) {
        const palCodeIndex = data.palCode.findIndex(pc => pc === palCode);
        if (palCodeIndex !== -1 && data.palCodeType[palCodeIndex]) {
          return data.palCodeType[palCodeIndex];
        }
      }

      // Final fallback
      return data.palCodeType && data.palCodeType.length > 0
        ? data.palCodeType[0]
        : '-';
    };

    // Create individual table header for each combination
    const createTableHeader = (palCode, accountCard, palCodeAccountGroup, palCodeCategory, accountCardId) => {
      const accountLabel = showAccountCodeId && accountCardId && accountCardId !== '-'
        ? `${accountCardId} ${accountCard || '-'}`.trim()
        : (accountCard || '-');

      return `
        <div class="table-header">
          <div class="titles-details">
            <div style="text-align:right">
              <div class="bold-u">${palCode || "-"}</div>
              <div class="bold-u">${accountLabel}</div>
              <div class="bold-u">${new Date(data.startDate).getFullYear()}</div>
            </div>
            <div>
              <div class="bold-u">${palCodeAccountGroup || "-"}</div>
              <div class="bold-u">${palCodeCategory || "-"}</div>

            </div>
          </div>
        </div>
      `;
    };


    /* ==== CLICKABLE COLUMN HEADER ================================= */
    const headerCell = (label, key, type) => `
  <th class="th-sort" data-header="${key}"
      onclick="window.parent.dispatchEvent(new CustomEvent('headerSortUpdate',{detail:decodeURIComponent('${encodeURIComponent(
      JSON.stringify({ key, type })
    )}')}))">
    ${label}
  </th>`;

    const columnHeader = `
      <tr class="columns">

        ${headerCell("פרטים", "description", "string")}
        ${headerCell("(פקודה)&nbsp;<br>חודש/שנה", "command", "string")}
        ${headerCell("אסמכתא&nbsp;1", "reference1", "string")}
        ${headerCell("אסמכתא&nbsp;2", "reference2", "string")}
        ${headerCell("תאריך&nbsp;מסמך", "documentDate", "date")}
        ${headerCell("תאריך&nbsp;רישום", "uploadDate", "date")}
        ${headerCell("חשבון&nbsp;נגדי", "countraAccount", "string")}
        ${renderAmountHeaderCells()}
      </tr>

      </tr>`;

    /* ==== ROWS ==================================================== */
    const rows = sorted
      .map(
        (tr, i) => `
      <tr${i === sorted.length - 1 || sorted.length === 1 ? ' class="tr-final"' : ""}>
        <td class="desc" title="${(tr.description || '').replace(/"/g, '&quot;')}">${truncateDesc(tr.description)}</td>
        <td>${tr.command}</td>
        <td>${tr.reference1 ?? ""}</td>
        <td>${tr.reference2 ?? ""}</td>
        <td>${new Date(tr.documentDate).toLocaleDateString("he-IL")}</td>
        <td>${new Date(tr.uploadDate).toLocaleDateString("he-IL")}</td>
        <td>${tr.countraAccount || "-"}</td>
        ${renderTransactionAmountCells(tr)}
          </tr>`
          )
          .join("");




    const initialDataRow = `
                <tr class="${sorted.length === 0 ? 'tr-final' : 'initial-row'}">
                  <td class="desc" title="${(t(initialData?.description) || '').replace(/"/g, '&quot;')}">${truncateDesc(t(initialData?.description))}</td>
                  <td class="center muted"></td>
                  <td class="center muted"></td>
                  <td class="center muted"></td>
                  <td class="center muted"></td>
                  <td class="center muted"></td>
                  <td class="center muted">—</td>
                  ${renderOpeningAmountCells(derivedInitialDebit, derivedInitialCredit, initialData?.balance || 0, initialData?.foreignCurrencyInitialBalance ?? initialData?.foreignCurrencyBalance ?? 0)}
                </tr>`;

    

    // Generate combinations based on accountCards from the response
    const generateCombinations = () => {
      const combinations = [];

      // Determine which cards to use: selected cards if available, otherwise all cards
      let cardsToUse = [];
      
      if (data.accountCards && Array.isArray(data.accountCards)) {
        // If there are selected cards, filter to only show selected ones
        if (selectedCardsForReport && selectedCardsForReport.length > 0) {
          // Extract accountCardIds from selected cards
          const selectedCardIds = selectedCardsForReport.map(card => 
            card.accountCardId || card.cardId
          ).filter(Boolean);
          
          // Filter accountCards to only include selected ones
          cardsToUse = data.accountCards.filter(ac => 
            selectedCardIds.includes(ac.accountCardId)
          );
        } else {
          // No selection - use all cards
          cardsToUse = data.accountCards;
        }

        // Find the first account card with a valid PAL code to use as reference
        const referenceAccountCard = cardsToUse.find(ac => ac.palCode && ac.palCode.trim() !== '');
        const referencePalCode = referenceAccountCard ? referenceAccountCard.palCode : null;

        // New format: create a combination for each account card
        cardsToUse.forEach(accountCard => {
          // Get the specific PAL code details for this account card
          let palCodeAccountGroup = "-";
          let palCodeCategory = "-";
          let effectivePalCode = accountCard.palCode && accountCard.palCode.trim() !== '' ? accountCard.palCode : referencePalCode || "-";

          // Use the enhanced PAL code details from the account card if available
          if (accountCard.palCodeAccountGroup && accountCard.palCodeAccountGroup !== 'N/A') {
            palCodeAccountGroup = accountCard.palCodeAccountGroup;
          }

          if (accountCard.palCodeCategory && accountCard.palCodeCategory !== 'N/A') {
            palCodeCategory = accountCard.palCodeCategory;
          }

          // Fallback: try to find the PAL code details from the palCodeCategoryOptions
          if ((palCodeAccountGroup === "-" || palCodeCategory === "-") && effectivePalCode && effectivePalCode !== "-" && palCodeCategoryOptions.length > 0) {
            const palCodeDetails = palCodeCategoryOptions.find(cat =>
              cat.palCodeCategoryId === effectivePalCode
            );
            if (palCodeDetails) {
              if (palCodeAccountGroup === "-") {
                palCodeAccountGroup = palCodeDetails.accountingGroup || "-";
              }
              if (palCodeCategory === "-") {
                palCodeCategory = palCodeDetails.palCodeCategoryId || "-";
              }
            }
          }

          // If still not found, try to match from the array-based response data
          if (palCodeAccountGroup === "-" || palCodeCategory === "-") {
            // Find the index of this account card's PAL code in the arrays
            const palCodeIndex = data.palCode && Array.isArray(data.palCode)
              ? data.palCode.findIndex(pal => pal === effectivePalCode)
              : -1;

            if (palCodeIndex !== -1) {
              // Get the corresponding values from the same index in other arrays
              if (palCodeCategory === "-" && data.palCodeCategory && Array.isArray(data.palCodeCategory)) {
                palCodeCategory = data.palCodeCategory[palCodeIndex] || "-";
              }

              if (palCodeAccountGroup === "-" && data.palCodeAccountGroup && Array.isArray(data.palCodeAccountGroup)) {
                palCodeAccountGroup = data.palCodeAccountGroup[palCodeIndex] || "-";
              }
            }
          }

          // Final fallback to first values if still not found
          if (palCodeAccountGroup === "-" && data.palCodeAccountGroup) {
            palCodeAccountGroup = Array.isArray(data.palCodeAccountGroup)
              ? data.palCodeAccountGroup[0] || "-"
              : data.palCodeAccountGroup;
          }

          if (palCodeCategory === "-" && data.palCodeCategory) {
            palCodeCategory = Array.isArray(data.palCodeCategory)
              ? data.palCodeCategory[0] || "-"
              : data.palCodeCategory;
          }

          const combination = {
            palCode: effectivePalCode,
            accountCard: accountCard.accountCardName || "-",
            accountCardId: accountCard.accountCardId || "-",
            palCodeAccountGroup: palCodeAccountGroup,
            palCodeCategory: palCodeCategory,
            // Store the specific account card data for this combination
            accountCardData: accountCard
          };



          combinations.push(combination);
        });
      } else {
        // Fallback to old format
        const palCodes = data.palCode && Array.isArray(data.palCode) ? data.palCode : [data.palCode || "-"];
        const accountCards = data.accountCard ? [data.accountCard] : ["-"];

        const palCodeAccountGroups = data.palCodeAccountGroup && Array.isArray(data.palCodeAccountGroup) ? data.palCodeAccountGroup : [data.palCodeAccountGroup || "-"];
        const palCodeCategories = data.palCodeCategory && Array.isArray(data.palCodeCategory) ? data.palCodeCategory : [data.palCodeCategory || "-"];

        // Create combinations based on the length of arrays
        const maxLength = Math.max(palCodes.length, accountCards.length, palCodeAccountGroups.length, palCodeCategories.length);

        for (let i = 0; i < maxLength; i++) {
          combinations.push({
            palCode: palCodes[i] || palCodes[0] || "-",
            accountCard: accountCards[i] || accountCards[0] || "-",
            accountCardId: data.accountCardId || "-",
            palCodeAccountGroup: palCodeAccountGroups[i] || palCodeAccountGroups[0] || "-",
            palCodeCategory: palCodeCategories[i] || palCodeCategories[0] || "-",
            accountCardData: null
          });
        }
      }

      return combinations;
    };

    const combinations = generateCombinations();

    // Generate tables for each combination
    const generateTables = () => {
      return combinations.map((combination, index) => {
        // Get transactions for this specific account card
        let tableTransactions = [];

        if (combination.accountCardData && combination.accountCardData.transactions) {
          tableTransactions = combination.accountCardData.transactions;
        } else {
          tableTransactions = data.transactions || [];
        }

        if (fcOnly) {
          tableTransactions = tableTransactions.filter(fcRowHasActivity);
        }

        // Check if card has initial balance (initialDebit or initialCredit)
        let hasInitialBalance = false;
        let initialDebit = 0;
        let initialCredit = 0;
        
        if (combination.accountCardData) {
          // Use initialDebit/initialCredit if available, otherwise calculate from balance
          initialDebit = combination.accountCardData.initialDebit !== undefined 
            ? combination.accountCardData.initialDebit 
            : (combination.accountCardData.balance >= 0 ? combination.accountCardData.balance : 0);
          initialCredit = combination.accountCardData.initialCredit !== undefined 
            ? combination.accountCardData.initialCredit 
            : (combination.accountCardData.balance < 0 ? Math.abs(combination.accountCardData.balance) : 0);
          hasInitialBalance = initialDebit !== 0 || initialCredit !== 0;
        }

        // FC opening balance for this account card (single-currency scope).
        const fcInitialBalance = combination.accountCardData
          ? (combination.accountCardData.foreignCurrencyInitialBalance ?? 0)
          : 0;
        const hasFcInitialBalance = fcOnly && fcInitialBalance !== 0;

        // Skip this table only if it has no transactions AND no initial balance
        if ((!tableTransactions || tableTransactions.length === 0) && !(fcOnly ? hasFcInitialBalance : hasInitialBalance)) {
          return '';
        }

        // Create table header
        const tableHeader = createTableHeader(
          combination.palCode,
          combination.accountCard,
          combination.palCodeAccountGroup,
          combination.palCodeCategory,
          combination.accountCardId
        );

        // Create initial data row for this account card
        let tableInitialData = '';
        if (combination.accountCardData && (fcOnly ? hasFcInitialBalance : hasInitialBalance)) {
          const accountCardBalance = initialDebit - initialCredit;

          tableInitialData = `
            <tr class="${tableTransactions.length === 0 ? 'tr-final' : 'initial-row'}">
              <td class="desc" title="${(t(combination.accountCardData.description || 'OpeningBalance', { defaultValue: combination.accountCardData.description || 'Opening Balance' }) || '').replace(/"/g, '&quot;')}">${truncateDesc(t(combination.accountCardData.description || 'OpeningBalance', { defaultValue: combination.accountCardData.description || 'Opening Balance' }))}</td>
              <td class="center muted"></td>
              <td class="center muted"></td>
              <td class="center muted"></td>
              <td class="center muted"></td>
              <td class="center muted"></td>
              <td class="center muted">—</td>
              ${renderOpeningAmountCells(initialDebit, initialCredit, accountCardBalance, fcInitialBalance)}
            </tr>`;
        } else if (!combination.accountCardData) {
          tableInitialData = initialDataRow;
        }

        // Sort transactions for this table
        const sortedTableTransactions = sortBy(
          tableTransactions,
          sortConfig.key,
          sortConfig.type,
          sortConfig.dir
        );

        // Generate rows for this table
        const tableRows = sortedTableTransactions
          .map(
            (tr, i) => `
          <tr${i === sortedTableTransactions.length - 1 ? ' class="tr-final"' : ""}>
            <td class="desc" title="${(tr.description || '').replace(/"/g, '&quot;')}">${truncateDesc(tr.description)}</td>
            <td class="center">${tr.command}</td>
            <td class="center">${tr.reference1 ?? ""}</td>
            <td class="center muted">${tr.reference2 ?? ""}</td>
            <td class="center nowrap">${new Date(tr.documentDate).toLocaleDateString("he-IL")}</td>
            <td class="center nowrap">${new Date(tr.uploadDate).toLocaleDateString("he-IL")}</td>
            <td class="center">${tr.countraAccount || "-"}</td>
            ${renderTransactionAmountCells(tr)}
          </tr>`
          )
          .join("");

        // Calculate totals for this specific account card
        // Total debit/credit SHOULD include opening balance (initial amounts + transactions)
        const totalDebitAmount = tableTransactions.reduce(
          (acc, cur) => acc + (cur.debitAccount || 0),
          0
        ) + initialDebit;

        const totalCreditAmount = tableTransactions.reduce(
          (acc, cur) => acc + (cur.creditAccount || 0),
          0
        ) + initialCredit;

        // Final balance SHOULD include opening balance
        const finalBal = totalDebitAmount - totalCreditAmount;

        // Foreign-currency totals (single-currency scope), including the FC opening balance.
        const fcTotalDebit = tableTransactions.reduce(
          (acc, cur) => acc + (cur.foreignCurrencyDebit || 0),
          0
        ) + (fcInitialBalance > 0 ? fcInitialBalance : 0);
        const fcTotalCredit = tableTransactions.reduce(
          (acc, cur) => acc + (cur.foreignCurrencyCredit || 0),
          0
        ) + (fcInitialBalance < 0 ? Math.abs(fcInitialBalance) : 0);
        const fcFinalBal = fcTotalDebit - fcTotalCredit;
        const totalsAmountCells = renderTotalsAmountCells(
          totalDebitAmount, totalCreditAmount, finalBal,
          fcTotalDebit, fcTotalCredit, fcFinalBal
        );
        const totalsAmountCellsFinal = renderTotalsAmountCells(
          totalDebitAmount, totalCreditAmount, finalBal,
          fcTotalDebit, fcTotalCredit, fcFinalBal,
          'triple-underline2'
        );

        // Create dynamic table totals block for this specific combination
        // Filler cells for the journal-detail columns (month, ref1, ref2, date2, date1, acc)
        // so the totals land in the amount columns at the end.
        const totalsFillerCells = '<td></td><td></td><td></td><td></td><td></td><td></td>';

        const tableTotalsBlock = `
          <div class="totals-block">
          <table class="totals-table" dir="rtl">
            <colgroup>
              <col class="c-details" />
              <col class="c-month" />
              <col class="c-ref1" />
              <col class="c-ref2" />
              <col class="c-date2" />
              <col class="c-date1" />
              <col class="c-acc" />
              ${amountColGroup}
            </colgroup>
            <tbody>
              <tr>
                <td class="overflowed">סה"כ ${combination.accountCard || 'N/A'}</td>
                ${totalsFillerCells}
                ${totalsAmountCells}
              </tr>
              <tr>
                <td class="overflowed">סה"כ ${combination.palCode || 'N/A'}</td>
                ${totalsFillerCells}
                ${totalsAmountCells}
              </tr>
              <tr>
                <td class="overflowed">סה"כ ${getPalCodeType(combination.palCode, combination.accountCard)}</td>
                ${totalsFillerCells}
                ${totalsAmountCells}
              </tr>
              <tr>
                <td class="overflowed">סה"כ לדוח</td>
                ${totalsFillerCells}
                ${totalsAmountCellsFinal}
              </tr>
            </tbody>
          </table>
          </div>`;

        return `

          <table dir="rtl" ${index > 0 ? 'class="page-break-before"' : ''}>
            <colgroup>
              <col class="c-details" />
              <col class="c-month" />
              <col class="c-ref1" />
              <col class="c-ref2" />
              <col class="c-date2" />
              <col class="c-date1" />
              <col class="c-acc" />
              ${amountColGroup}
            </colgroup>
            <thead>
            <tr>
              <td colspan="${colCount}" style="padding: 0; border: none;">
                <div style="text-align: center; margin-bottom: 5px;">
                  <div class="dates-line-wrapper">
                    <div class="dates-line" style="font-size: 14pt; font-weight: bold; color: #333; text-align: center; ${index === 0 ? '' : 'margin: 22px 0;'}">${getPalCodeType(combination.palCode, combination.accountCard)}</div>
                  </div>
                </div>
                </div>
               </td>
            </tr>
              <tr>
                <td colspan="${colCount}" style="padding: 0; border: none;">
                  ${tableHeader}
                </td>
            </tr>

              ${columnHeader}
            </thead>
            <tbody>
            ${tableInitialData}
            ${tableRows}
            </tbody>
          </table>
          ${tableTotalsBlock}
        `;
      }).join('');
    };

    /* ==== FINAL HTML ============================================== */
    return `
<!doctype html>
<html dir="rtl" lang="he">
<head>
<meta charset="utf-8" />
<title>AccountCard Report</title>
<style>
@font-face {
  font-family: "NotoSansHebrew";
  src: url(data:font/ttf;base64,${hebrewFont}) format("truetype");
  font-weight: 400;
}

@page {
  size: ${useLandscape ? 'A4 landscape' : 'A4'};
  @top-left {
    content: "עמוד " counter(page) " מתוך " counter(pages);
    font-size: 9px;
    font-weight: 600;
    color: #333;
    margin-top:1px;
    margin-left: 2px;
    background: none;
  }
}

:root{
  --border:#000;          /* black rules / underlines */
  --hair:#d1d5db;         /* thin row separators */
  --text:#111827;
  --muted:#6b7280;
}

html,
body,
table,
th,
td {
    color: #000 !important;
    /* force black text in PDF */
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}

body {
    margin: 45px 8px 8px 8px;
    font-family: Arial, sans-serif ;
}

/* Development Pattern Wrapper */
.dev-pattern-wrapper {
    border: 2px solid #0066cc;
    border-radius: 8px;
    padding: 18px;
    margin: 8px;
}



  
  .page-break {
      page-break-before: always;
  }
  
  .page-number::before {
      content: counter(page);
  }
  
  .total-pages::before {
      content: counter(pages);
  }
  

.upper-name-div {
  display: -webkit-box;              /* Old syntax for older WebKit browsers */
  display: -moz-box;                 /* Old syntax for Firefox */
  display: -ms-flexbox;              /* Old syntax for IE10 */
  display: -webkit-flex;             /* Newer WebKit syntax */
  display: flex;                     /* Standard syntax */
  
  -webkit-box-orient: horizontal;    /* Ensures horizontal layout in older WebKit */
  -moz-box-orient: horizontal;       /* Ensures horizontal layout in older Firefox */
  -webkit-flex-direction: row;       /* Flexbox row direction for WebKit */
  -ms-flex-direction: row;           /* Flexbox row direction for IE10 */
  flex-direction: row;                /* Standard syntax */
  direction: rtl;  
  justify-content: space-between;    /* Space between left and right content */
  align-items: center;               /* Keep left + right on the same baseline row */
  width: 100%;
  padding: 5px 8px !important;
  box-sizing: border-box;
}

.left-header-info {
  display: flex;
  flex-direction: row;
  align-items: center;
  text-align: left;
  direction: ltr;
  margin: 0;
  padding: 0;
}

.right-header-info {
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  text-align: right;
  direction: rtl;
  margin: 0;
  padding: 0;
}

.current-datetime {
  font-size: 12px;
  color: #333;
  font-weight: 800;
  margin: 0;
  padding: 0;
  line-height: 1.2;
  direction: ltr;
}

.first {
  margin: 0;
  padding: 0;
  font-size: 12px;
  font-style: normal;
  font-weight: 800;
  line-height: 1.2;
}




.sums-of-transactions {
  font-size: 16px;
  font-weight: 1000;
  text-align: right;
}


.row-top {
  display: -webkit-box;
  display: -moz-box;
  display: -ms-flexbox;
  display: -webkit-flex;
  display: flex;

  -webkit-box-orient: horizontal;
  -moz-box-orient: horizontal;
  -webkit-box-direction: reverse;
  -moz-box-direction: reverse;
  -webkit-flex-direction: row-reverse;
  -ms-flex-direction: row-reverse;
  flex-direction: row-reverse;

  justify-content: space-between;
  font-size: 9pt;
  font-weight: 600;
  padding: 0 20px;
}

.client-line {
  text-align: right;
  margin-left: auto;
  order: 2;
}

.report-title-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 1pt 0;
  position: relative;
}

.report-title {
  text-align: center;
  font-size: 18pt;
  font-weight: 800;
  margin: 0 0 10pt 0;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  text-decoration: underline;
  text-decoration-color: #000;
  color: #000;
}

.print-header-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  font-size: 9pt;
  font-weight: 600;
  line-height: 1.2;
  width: 120px;
  position: absolute;
  left: 0;
  top: 0;
}

.print-header-info.hidden {
  display: none;
}



.page-info {
  font-weight: 600;
}

.spacer {
  width: 120px;
}

.dates-line {
  display: -webkit-box;
  display: -moz-box;
  display: -ms-flexbox;
  display: -webkit-flex;
  display: flex;

  -webkit-box-orient: horizontal;
  -moz-box-orient: horizontal;
  -webkit-box-direction: reverse;
  -moz-box-direction: reverse;
  -webkit-flex-direction: row-reverse;
  -ms-flex-direction: row-reverse;
  flex-direction: row-reverse;

  justify-content: center;
  gap: 20px;
  font-size: 10pt;
  font-weight: 700;
  text-decoration: underline;
  text-decoration-color: #000;
}
.initial-row{
font-weight: bold;

}
.card-range-line {
  display: -webkit-box;
  display: -moz-box;
  display: -ms-flexbox;
  display: -webkit-flex;
  display: flex;

  -webkit-box-orient: horizontal;
  -moz-box-orient: horizontal;
  -webkit-box-direction: reverse;
  -moz-box-direction: reverse;
  -webkit-flex-direction: row-reverse;
  -ms-flex-direction: row-reverse;
  flex-direction: row-reverse;

  justify-content: center;
  gap: 20px;
  font-size: 10pt;
  font-weight: 700;
  text-decoration: underline;
  text-decoration-color: #000;
  margin-top: 4pt;
}

/* Single range item (card only or section only) — keep truly centered */
.card-range-line.single-line {
  justify-content: center;
  gap: 0;
  text-align: center;
}

.card-range-line.single-line .u {
  display: inline-block;
}

/* Equal columns: label + date stacked & centered under each other */
.header-meta-grid {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  gap: 12px;
  margin-top: 7pt;
  width: 100%;
  direction: rtl;
}

.header-meta-grid.single-col {
  gap: 0;
}

.header-meta-col-left {
  display: flex;
  flex-direction: column;
  text-align: left;
  flex: 0 1 auto;
}
.header-meta-col-right {
  display: flex;
  flex-direction: column;
  text-align: right;
  flex: 0 1 auto;
}
.header-meta-label,
.header-meta-date {
  display: block;
  font-size: 10pt;
  font-weight: 700;
  line-height: 1.35;
  white-space: nowrap;
  color: #000;
  text-decoration: underline;
  text-decoration-color: #000;
}

.header-meta-date {
  margin-top: 2pt;
}

.dash {
  text-align: center;
  margin: 6pt 0;
  font-size: 10pt;
}

.titles-details {
  display: -webkit-box;
  display: -moz-box;
  display: -ms-flexbox;
  display: -webkit-flex;
  display: flex;

  -webkit-box-pack: justify;
  -moz-box-pack: justify;
  -ms-flex-pack: justify;
  -webkit-justify-content: space-between;
  justify-content: space-between;

  font-size: 10pt;
  font-weight: 700;
  text-decoration: underline;
  text-decoration-color: #000;

}

.additional-details {
  display: -webkit-box;
  display: -moz-box;
  display: -ms-flexbox;
  display: -webkit-flex;
  display: flex;

  -webkit-box-pack: justify;
  -moz-box-pack: justify;
  -ms-flex-pack: justify;
  -webkit-justify-content: space-between;
  justify-content: space-between;

  font-size: 10pt;
  font-weight: 700;
  text-decoration: underline;
  text-decoration-color: #000;
  padding: 0 20px;
  margin-top: 4pt;
}

.table-header {

  margin-bottom: 8pt;
}

table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 18px;
}

thead th,
tbody td {
  font-size: clamp(6pt, 1.2vw, 10pt);
  padding: 3pt 2pt;
  white-space: normal; /* allow wrapping */
  overflow: visible;
  text-overflow: unset;
  word-break: break-word;

}

thead th {
  text-align: center;
  padding: 4px 2px;
  border-bottom: none;
  font-weight: 700;
  white-space: normal;
  word-break: break-word;
  position: relative;
  background: #fff !important;
  background-color: #fff !important;
}

/* Individual underlines for each th - sliced effect (same black as totals) */
thead th::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 2px;
  right: 2px;
  height: 1px;
  background: #000 !important;
}

/* Remove the pseudo-element for empty th cells in second row */
thead tr:nth-child(2) th:empty::after {
  display: none;
}

tbody td {
  vertical-align: middle;
  font-size: 11px;
}


tbody tr.tr-final {
  font-size: 16px;
  // font-weight: 700;
  border-bottom: none;
  margin-top: 0;
}

tbody tr.tr-final td {
  position: relative;
  text-align: right;
  font-weight: bold;
}

/* Individual underlines for each td in tr-final - sliced effect like thead th */
tbody tr.tr-final td::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 2px;
  right: 2px;
  height: 1px;
  background: #000 !important;
}

/* Keep numbers aligned and LTR inside RTL table */
.num {  direction: rtl; font-variant-numeric: tabular-nums; }
.muted { color: var(--muted); }
.nowrap { white-space: nowrap; }

/* Truncate long descriptions so the table stays compact */
td.desc {
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

col.c-details {
  width: 14%;
}

.totals-table col.c-details {
  width: 14%;
}

.td-header,
.th-sort {
  cursor: pointer;
  user-select: none;
  text-align: right;
  font-size: 12px;
  background: #fff !important;
  background-color: #fff !important;
}

/* Keep the same clean white look as multi-card (no tinted underline row) */
.th-sort:hover,
.th-sort:focus,
.th-sort:active {
  background: #fff !important;
  background-color: #fff !important;
}

/* Description column header should be left-aligned */
.th-sort[data-header="description"] {
  text-align: left;
}

/* All description column data cells should be left-aligned */
td.desc {
  text-align: left;
}

/* Amount columns now sit at the end of the row; keep them right-aligned/LTR */
tbody tr.tr-final td:last-child {
  text-align: right;
}

.totals-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  margin-top: 5pt;

  /* underline config */
  --count: 3;           /* number of lines */
  --line: 1px;          /* each line thickness */
  --gap: 1px;           /* gap between lines */
  --border: #000;       /* fallback if not defined elsewhere */
}

/* Keep all 4 total rows together; never split under the fixed print header */
.totals-block,
.totals-table,
.totals-table tbody,
.totals-table tr {
  page-break-inside: avoid !important;
  break-inside: avoid !important;
}

.totals-block {
  /* keep totals with the card above when space allows */
  page-break-before: avoid;
  break-before: avoid;
}

.totals-table td {
  font-weight: 600;
  border-top: none;
  position: relative;
  font-size: 8pt;
  text-align: right !important;
}
.triple-underline2 {
  position: absolute;
  bottom: 0;
  left: 2px;
  right: 2px;
  height: 6px; /* 3 lines + 2 gaps — match .triple-underline */
  pointer-events: none;
  border-bottom: 1px solid #000;
  background: transparent !important;
}
.triple-underline2::before,
.triple-underline2::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background: #000 !important;
}
.triple-underline2::before {
  top: 0;
}

.triple-underline2::after {
  top: 3px;
} 
/* Triple underline using actual HTML elements for better print compatibility */
.triple-underline {
  position: absolute;
  bottom: 0;
  left: 2px;
  right: 2px;
  height: 6px; /* 3 lines + 2 gaps */
  pointer-events: none;
  border-bottom: 1px solid #000;
  background: transparent !important;
}

.triple-underline::before,
.triple-underline::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background: #000 !important;
}

.triple-underline::before {
  top: 0;
}

.triple-underline::after {
  top: 3px;
}

/* Ensure td-last-sum cells have relative positioning for absolute positioned underlines */
.totals-table td.td-last-sum,
td.td-last-sum {
  position: relative;
  padding-bottom: 6px; /* Add space between text and underlines */
  background: #fff !important;
  background-color: #fff !important;
}


.td-last-sum {
  text-align: left !important;
  direction: ltr !important;
  font-variant-numeric: tabular-nums;

  margin-bottom: 5px !important;
}

.overflowed {
  text-align: right;
  white-space: normal;
  direction: rtl;
  word-break: break-word;
  width: 100px;
  height: 10px !important;
}

/* Tiny helper to visually indent descriptive lines */

#header {
  position: fixed;
  top: 0px;
  left: 0px;
  width: 100%;
  z-index: 1000;

  }
.u,
.bold-u {
  text-decoration: underline;
  text-decoration-color: #000 !important;
  color: #000;
}


/* Print header info visibility */
.print-header-info {
  display: none;
}

.print-header-info:not(.hidden) {
  display: flex;
}

/* Ensure header has a background so it prints clearly */
.header-wrapper { background: #fff }
@media print {

  
 .screen-only { display: block; }
 .print-only  { display: none; } 
  .header-wrapper {
    page-break-inside: avoid;
    display: block !important;
    visibility: visible !important;
  }

  /* Keep top margin so title/range (header-wrapper) clears fixed #header — same as old layout */
  body {
    margin: 45px 8px 8px 8px !important;
  }
  
  div#header {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    padding: 2px 4px !important;
    width: 100% !important;
    z-index: 1000 !important;
    -webkit-print-color-adjust: exact !important;
    color-adjust: exact !important;
    background-color: #fff !important;
  }

  .totals-block,
  .totals-table,
  .totals-table tr {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }

  /* Keep client info + datetime on one aligned row when printing */
  .upper-name-div {
    display: flex !important;
    flex-direction: row !important;
    justify-content: space-between !important;
    align-items: center !important;
    width: 100% !important;
    padding: 5px 8px !important;
    margin: 0 !important;
  }

  .left-header-info,
  .right-header-info {
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .right-header-info .first,
  .left-header-info .current-datetime {
    font-size: 11px !important;
    font-weight: 800 !important;
    line-height: 1.2 !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  /* Show print header info in print mode */
  .print-header-info {
    display: flex !important;
    position: absolute !important;
    left: 0 !important;
    top: 0 !important;
  }
  .dev-pattern-wrapper{
    border: none !important;
    margin: 0 !important;
    padding: 0 !important;
    
    margin-top: 5px !important; /* Add space after fixed header */
  }
  .report-title-container {
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    position: relative !important;
    margin-top: 5px !important;
  }
  
  .report-title {
    position: absolute !important;
    left: 50% !important;
    transform: translateX(-50%) !important;
  }
  
  /* Page header for every page */
  .page-header {
    position: running(page-header);
    width: 100%;
    padding: 5pt 20pt;
    font-size: 9pt;
    font-weight: 600;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #fff;
  }
  
  .page-header .current-datetime {
    margin: 0;
  }
  
  .page-header .page-info {
    margin: 0;
  }

.first-table-dates-line{
  body {
    margin: 5pt;
    font-size: 10pt;
    color: #000 !important;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
  }

  thead th {
    font-size: 9pt !important;
    padding: 2pt 3pt !important;
  }

  td {
    padding: 2pt 3pt !important;
    font-size: 10pt !important;
  }

  .tr:hover,
  .tr:nth-child(even) {
    background: transparent !important;
  }



  .first-table-dates-line{
    margin-top: 80px !important;
  }
  /* Ensure triple underlines print correctly — same black for single & multi card */
  thead th,
  .th-sort,
  td.td-last-sum {
    background: #fff !important;
    background-color: #fff !important;
  }

  thead th::after {
    background: #000 !important;
  }

  .triple-underline,
  .triple-underline2 {
    background: transparent !important;
    -webkit-print-color-adjust: exact !important;
    color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  
  .triple-underline::before,
  .triple-underline::after,
  .triple-underline2::before,
  .triple-underline2::after {
    background: #000 !important;
    -webkit-print-color-adjust: exact !important;
    color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  
  .client-line {
    text-align: right !important;
    margin-left: auto !important;
    order: 2 !important;
  }

  td.desc {
    max-width: 140px !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
  }

}



</style>
</head>
<body>
<!-- PRINT header (fixed; repeated on every page) -->

<div class="dev-pattern-wrapper">
  <!-- SCREEN header (shown once on screen / hidden in print) -->
  ${createMainHeader()}
  <div class="table-wrapper">
  ${generateTables()}
  </div>
</div>

</body>
</html>`;
  }, [data, hebrewFont, sortConfig, t, selectedCardsForReport, selectedCurrency, showAccountCodeId]);

  const {
    reportLanguage,
    setReportLanguage,
    isTranslating,
    displayHtml,
    previewDir,
    translateHtml,
    translateWb,
  } = useReportTranslation(reportHtmlContent, token);

  const formatExcelDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  /* ------------ ACTION HANDLERS ------------ */
  // Build an Excel workbook that mirrors the on-screen/PDF report: a title block
  // with the full period + client details, then one section per account card
  // (card header, opening balance, its transactions, and its totals).
  const downloadExcel = async () => {
    if (!data) return;

    /* ---- Foreign-currency setup (mirrors reportHtmlContent) ---- */
    const showFc = selectedCurrency !== null && selectedCurrency !== undefined;
    const fcOnly = typeof selectedCurrency === 'number';
    const fcCodeName = Object.entries(Constants.ForeignCurrencyCode || {})
      .find(([, value]) => value === selectedCurrency)?.[0];
    const fcSymbol = fcCodeName ? (Constants.ForeignCurrencySymbol?.[fcCodeName] || fcCodeName) : '';
    const fcLabel = fcSymbol ? `מט"ח (${fcSymbol})` : 'מט"ח';

    const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
    const sumField = (list, key) => list.reduce((acc, cur) => acc + (Number(cur[key]) || 0), 0);

    const foldFcOpening = (fcDebit, fcCredit, fcBalance) => {
      const d = Number(fcDebit) || 0;
      const c = Number(fcCredit) || 0;
      const b = Number(fcBalance) || 0;
      if (b === 0 && d === 0 && c === 0) return { debit: d, credit: c, balance: b };
      const opening = b - d + c;
      return { debit: d + (opening > 0 ? opening : 0), credit: c + (opening < 0 ? -opening : 0), balance: b };
    };
    const fcRowHasActivity = (tr) =>
      (Number(tr?.foreignCurrencyDebit) || 0) !== 0 ||
      (Number(tr?.foreignCurrencyCredit) || 0) !== 0 ||
      (Number(tr?.foreignCurrencyBalance) || 0) !== 0;

    /* ---- Columns: journal details first, amounts last ---- */
    const detailCols = [
      { label: t('פרטים'), width: 36 },
      { label: t('פקודה חודש/שנה'), width: 16 },
      { label: t('אסמכתא 1'), width: 14 },
      { label: t('אסמכתא 2'), width: 14 },
      { label: t('תאריך מסמך'), width: 14 },
      { label: t('תאריך רישום'), width: 14 },
      { label: t('חשבון נגדי'), width: 24 },
    ];
    const amountLabels = fcOnly
      ? [`חובה ${fcLabel}`, `זכות ${fcLabel}`, `יתרה ${fcLabel}`]
      : showFc
        ? ['חובה', 'זכות', 'יתרה', `חובה ${fcLabel}`, `זכות ${fcLabel}`, `יתרה ${fcLabel}`]
        : ['חובה', 'זכות', 'יתרה'];

    const detailCount = detailCols.length;
    const colCount = detailCount + amountLabels.length;
    const columnHeaderRow = [...detailCols.map((c) => c.label), ...amountLabels];

    /* ---- Amount cell builders (return arrays aligned to amountLabels) ---- */
    const txAmounts = (tr) => {
      if (fcOnly) {
        const f = foldFcOpening(tr.foreignCurrencyDebit, tr.foreignCurrencyCredit, tr.foreignCurrencyBalance);
        return [round2(f.debit), round2(f.credit), round2(f.balance)];
      }
      const base = [round2(tr.debitAccount), round2(tr.creditAccount), round2(tr.balance)];
      if (showFc) {
        return [...base, round2(tr.foreignCurrencyDebit), round2(tr.foreignCurrencyCredit), round2(tr.foreignCurrencyBalance)];
      }
      return base;
    };
    const openingAmounts = (initialDebit, initialCredit, balance, fcBalance) => {
      if (fcOnly) {
        const fcDebit = fcBalance > 0 ? fcBalance : 0;
        const fcCredit = fcBalance < 0 ? Math.abs(fcBalance) : 0;
        return [round2(fcDebit), round2(fcCredit), round2(fcBalance)];
      }
      const base = [round2(initialDebit), round2(initialCredit), round2(balance)];
      if (showFc) return [...base, round2(fcBalance), '', round2(fcBalance)];
      return base;
    };
    const totalsAmounts = (debit, credit, balance, fcDebit, fcCredit, fcBal) => {
      if (fcOnly) return [round2(fcDebit), round2(fcCredit), round2(fcBal)];
      const base = [round2(debit), round2(credit), round2(balance)];
      if (showFc) return [...base, round2(fcDebit), round2(fcCredit), round2(fcBal)];
      return base;
    };

    /* ---- Resolve which cards to show (mirrors generateCombinations) ---- */
    let filteredAccountCards = Array.isArray(data.accountCards) ? data.accountCards : [];
    const selectedCardIds = (selectedCardsForReport || [])
      .map((card) => card.accountCardId || card.cardId)
      .filter(Boolean);
    if (selectedCardIds.length > 0 && filteredAccountCards.length > 0) {
      filteredAccountCards = filteredAccountCards.filter((ac) => selectedCardIds.includes(ac.accountCardId));
    }

    const resolveGroupCategory = (ac) => {
      let palCodeAccountGroup = (ac.palCodeAccountGroup && ac.palCodeAccountGroup !== 'N/A') ? ac.palCodeAccountGroup : '-';
      let palCodeCategory = (ac.palCodeCategory && ac.palCodeCategory !== 'N/A') ? ac.palCodeCategory : '-';
      const effectivePalCode = ac.palCode && String(ac.palCode).trim() !== '' ? ac.palCode : '-';
      if ((palCodeAccountGroup === '-' || palCodeCategory === '-') && effectivePalCode !== '-' && palCodeCategoryOptions.length > 0) {
        const details = palCodeCategoryOptions.find((cat) => cat.palCodeCategoryId === effectivePalCode);
        if (details) {
          if (palCodeAccountGroup === '-') palCodeAccountGroup = details.accountingGroup || '-';
          if (palCodeCategory === '-') palCodeCategory = details.palCodeCategoryId || '-';
        }
      }
      return { palCodeAccountGroup, palCodeCategory, effectivePalCode };
    };

    // getPalCodeType helper (mirrors the HTML version).
    const getPalCodeType = (palCode, accountCardName) => {
      if (Array.isArray(data.accountCards)) {
        let match = data.accountCards.find((ac) => ac.palCode === palCode && ac.accountCardName === accountCardName);
        if (!match) match = data.accountCards.find((ac) => ac.palCode === palCode);
        if (!match) match = data.accountCards.find((ac) => ac.accountCardName === accountCardName);
        if (match && match.palCodeType) return match.palCodeType;
      }
      if (Array.isArray(data.palCode) && Array.isArray(data.palCodeType)) {
        const idx = data.palCode.findIndex((pc) => pc === palCode);
        if (idx !== -1 && data.palCodeType[idx]) return data.palCodeType[idx];
      }
      return data.palCodeType && data.palCodeType.length > 0 ? data.palCodeType[0] : '-';
    };

    const referenceAccountCard = filteredAccountCards.find((ac) => ac.palCode && String(ac.palCode).trim() !== '');
    const referencePalCode = referenceAccountCard ? referenceAccountCard.palCode : null;

    let combinations;
    if (filteredAccountCards.length > 0) {
      combinations = filteredAccountCards.map((ac) => {
        const { palCodeAccountGroup, palCodeCategory, effectivePalCode } = resolveGroupCategory(ac);
        return {
          palCode: effectivePalCode !== '-' ? effectivePalCode : (referencePalCode || '-'),
          accountCard: ac.accountCardName || '-',
          accountCardId: ac.accountCardId || '-',
          palCodeAccountGroup,
          palCodeCategory,
          accountCardData: ac,
        };
      });
    } else {
      // Legacy fallback: single combination built from top-level data.
      combinations = [{
        palCode: Array.isArray(data.palCode) ? (data.palCode[0] || '-') : (data.palCode || '-'),
        accountCard: data.accountCard || '-',
        accountCardId: data.accountCardId || '-',
        palCodeAccountGroup: Array.isArray(data.palCodeAccountGroup) ? (data.palCodeAccountGroup[0] || '-') : (data.palCodeAccountGroup || '-'),
        palCodeCategory: Array.isArray(data.palCodeCategory) ? (data.palCodeCategory[0] || '-') : (data.palCodeCategory || '-'),
        accountCardData: null,
      }];
    }

    /* ---- Report title / period block (mirrors createMainHeader) ---- */
    const reportYear = data.startDate ? new Date(data.startDate).getFullYear() : '';
    const cardIds = [...new Set(
      (filteredAccountCards.length > 0
        ? filteredAccountCards.map((ac) => ac.accountCardId)
        : combinations.map((c) => c.accountCardId)
      ).filter(Boolean)
    )].sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
    const cardRange = cardIds.length === 1
      ? cardIds[0]
      : cardIds.length > 1
        ? `${cardIds[0]} עד כרטיס ${cardIds[cardIds.length - 1]}`
        : '';
    const dateRangeText = `מתאריך ${formatReportDate(data.startDate)} עד תאריך ${formatReportDate(data.EndDate)}`;

    /* ---- Style palette (xlsx-js-style) ---- */
    const PALETTE = {
      primary: 'FF2F4BFF',
      primaryDark: 'FF1E308F',
      headerFont: 'FFFFFFFF',
      opening: 'FFEFF2FF',
      totalFill: 'FFD7DFFF',
      stripe: 'FFF6F8FF',
      text: 'FF1F2937',
      border: 'FFC3CBDB',
    };
    const thinBorder = { style: 'thin', color: { rgb: PALETTE.border } };
    const allThin = { top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder };
    const STYLES = {
      clientLine: { font: { bold: true, sz: 13, color: { rgb: PALETTE.text } }, alignment: { horizontal: 'right' } },
      reportTitle: { font: { bold: true, sz: 20, color: { rgb: PALETTE.primary } }, alignment: { horizontal: 'center' } },
      dateLine: { font: { bold: true, sz: 12, color: { rgb: PALETTE.text } }, alignment: { horizontal: 'center' } },
      rangeLine: { font: { italic: true, sz: 11, color: { rgb: PALETTE.text } }, alignment: { horizontal: 'center' } },
      cardType: { font: { bold: true, sz: 15, color: { rgb: PALETTE.primaryDark } }, alignment: { horizontal: 'right' } },
      cardName: { font: { bold: true, sz: 14, color: { rgb: PALETTE.primary } }, alignment: { horizontal: 'right' } },
      cardMeta: { font: { bold: true, sz: 11, color: { rgb: PALETTE.text } }, alignment: { horizontal: 'right' } },
      header: { font: { bold: true, sz: 12, color: { rgb: PALETTE.headerFont } }, fill: { fgColor: { rgb: PALETTE.primary } }, alignment: { horizontal: 'center', vertical: 'center', wrapText: true }, border: allThin },
      openingText: { font: { bold: true, sz: 11, color: { rgb: PALETTE.text } }, fill: { fgColor: { rgb: PALETTE.opening } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
      openingNum: { font: { bold: true, sz: 11, color: { rgb: PALETTE.text } }, fill: { fgColor: { rgb: PALETTE.opening } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
      dataText: { font: { sz: 11, color: { rgb: PALETTE.text } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
      dataNum: { font: { sz: 11, color: { rgb: PALETTE.text } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
      stripeText: { font: { sz: 11, color: { rgb: PALETTE.text } }, fill: { fgColor: { rgb: PALETTE.stripe } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
      stripeNum: { font: { sz: 11, color: { rgb: PALETTE.text } }, fill: { fgColor: { rgb: PALETTE.stripe } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
      totalText: { font: { bold: true, sz: 13, color: { rgb: PALETTE.primaryDark } }, fill: { fgColor: { rgb: PALETTE.totalFill } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
      totalNum: { font: { bold: true, sz: 13, color: { rgb: PALETTE.primaryDark } }, fill: { fgColor: { rgb: PALETTE.totalFill } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    };
    const ROW_HEIGHT = {
      clientLine: 22, reportTitle: 32, dateLine: 20, rangeLine: 18,
      cardName: 26, cardType: 22, cardMeta: 18, header: 26, opening: 20, data: 18, total: 24, blank: 8,
    };

    /* ---- Assemble the sheet rows ---- */
    const aoa = [];
    const merges = [];
    const rowMeta = [];
    const blankRow = () => new Array(colCount).fill('');
    const pushMerged = (text, kind) => {
      const r = aoa.length;
      const row = blankRow();
      row[0] = text;
      aoa.push(row);
      rowMeta.push({ kind });
      merges.push({ s: { r, c: 0 }, e: { r, c: Math.max(colCount - 1, 0) } });
    };
    const pushRow = (detailValues, amounts, kind) => {
      const row = [...detailValues, ...amounts];
      while (row.length < colCount) row.push('');
      aoa.push(row);
      rowMeta.push({ kind });
    };
    const pushBlank = () => { aoa.push(blankRow()); rowMeta.push({ kind: 'blank' }); };

    pushMerged(`${getCaseNumberLabel(data?.caseType)}: ${data.clientCase}    ${data.clientName}`, 'clientLine');
    pushMerged(`דוח כרטסת הנהלת חשבונות${showFc ? ` - ${fcLabel}` : ''}`, 'reportTitle');
    pushMerged(dateRangeText, 'dateLine');
    if (cardRange) pushMerged(`מכרטיס ${cardRange}`, 'rangeLine');
    pushBlank();

    combinations.forEach((combo) => {
      const acData = combo.accountCardData;

      let tableTransactions = acData && acData.transactions ? acData.transactions : (data.transactions || []);
      if (fcOnly) tableTransactions = tableTransactions.filter(fcRowHasActivity);

      // Opening balance
      let initialDebit = 0;
      let initialCredit = 0;
      let fcInitialBalance = 0;
      if (acData) {
        initialDebit = acData.initialDebit !== undefined ? acData.initialDebit : (acData.balance >= 0 ? acData.balance : 0);
        initialCredit = acData.initialCredit !== undefined ? acData.initialCredit : (acData.balance < 0 ? Math.abs(acData.balance) : 0);
        fcInitialBalance = acData.foreignCurrencyInitialBalance ?? 0;
      } else {
        initialDebit = initialData?.balance >= 0 ? initialData.balance : 0;
        initialCredit = initialData?.balance < 0 ? Math.abs(initialData.balance) : 0;
        fcInitialBalance = initialData?.foreignCurrencyInitialBalance ?? initialData?.foreignCurrencyBalance ?? 0;
      }
      const hasInitialBalance = initialDebit !== 0 || initialCredit !== 0;
      const hasFcInitialBalance = fcOnly && fcInitialBalance !== 0;

      // Skip cards without any activity (matches HTML behaviour).
      if ((!tableTransactions || tableTransactions.length === 0) && !(fcOnly ? hasFcInitialBalance : hasInitialBalance)) {
        return;
      }

      const palCodeType = getPalCodeType(combo.palCode, combo.accountCard);

      // Card section header lines (right-aligned above the table, organized).
      const accountLabel = showAccountCodeId && combo.accountCardId && combo.accountCardId !== '-'
        ? `${combo.accountCardId} - ${combo.accountCard || '-'}`
        : (combo.accountCard || '-');
      pushMerged(`${accountLabel}${combo.palCode ? `  (${combo.palCode})` : ''}`, 'cardName');
      pushMerged(`${palCodeType || ''}${reportYear ? `   ${reportYear}` : ''}`.trim() || '-', 'cardType');
      pushMerged(`${combo.palCodeAccountGroup || ''}   ${combo.palCodeCategory || ''}`.trim() || '-', 'cardMeta');

      // Column headers for this card.
      pushRow([...columnHeaderRow], [], 'header');

      // Opening balance row.
      if (fcOnly ? hasFcInitialBalance : (acData ? hasInitialBalance : true)) {
        const openingDesc = acData
          ? t(acData.description || 'OpeningBalance', { defaultValue: acData.description || 'Opening Balance' })
          : t(initialData?.description || 'OpeningBalance', { defaultValue: initialData?.description || 'Opening Balance' });
        const balance = acData ? (initialDebit - initialCredit) : (initialData?.balance || 0);
        pushRow([openingDesc, '', '', '', '', '', '—'], openingAmounts(initialDebit, initialCredit, balance, fcInitialBalance), 'opening');
      }

      // Transaction rows (sorted the same way as the table).
      const sortedTx = sortBy(tableTransactions, sortConfig.key, sortConfig.type, sortConfig.dir);
      sortedTx.forEach((tr, idx) => {
        pushRow(
          [
            tr.description ?? '',
            tr.command ?? '',
            tr.reference1 ?? '',
            tr.reference2 ?? '',
            formatExcelDate(tr.documentDate),
            formatExcelDate(tr.uploadDate),
            tr.countraAccount || '-',
          ],
          txAmounts(tr),
          idx % 2 === 1 ? 'stripe' : 'data'
        );
      });

      // Single consolidated total row.
      const totalDebit = sumField(tableTransactions, 'debitAccount') + initialDebit;
      const totalCredit = sumField(tableTransactions, 'creditAccount') + initialCredit;
      const finalBal = totalDebit - totalCredit;
      const fcTotalDebit = sumField(tableTransactions, 'foreignCurrencyDebit') + (fcInitialBalance > 0 ? fcInitialBalance : 0);
      const fcTotalCredit = sumField(tableTransactions, 'foreignCurrencyCredit') + (fcInitialBalance < 0 ? Math.abs(fcInitialBalance) : 0);
      const fcFinalBal = fcTotalDebit - fcTotalCredit;

      pushRow(
        [`סה"כ ${combo.accountCard || ''}`.trim(), '', '', '', '', '', ''],
        totalsAmounts(totalDebit, totalCredit, finalBal, fcTotalDebit, fcTotalCredit, fcFinalBal),
        'total'
      );

      pushBlank();
    });

    /* ---- Create worksheet ---- */
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!merges'] = merges.filter(Boolean);
    ws['!cols'] = [
      ...detailCols.map((c) => ({ wch: Math.max(c.width, 14) })),
      ...amountLabels.map(() => ({ wch: 18 })),
    ];
    ws['!rows'] = rowMeta.map((m) => ({ hpt: ROW_HEIGHT[m.kind] || 18 }));
    ws['!views'] = [{ RTL: true }];

    const mergedKinds = ['clientLine', 'reportTitle', 'dateLine', 'rangeLine', 'cardName', 'cardType', 'cardMeta'];
    aoa.forEach((row, r) => {
      const meta = rowMeta[r];
      if (!meta || meta.kind === 'blank') return;
      if (mergedKinds.includes(meta.kind)) {
        const addr = XLSX.utils.encode_cell({ r, c: 0 });
        if (ws[addr]) ws[addr].s = STYLES[meta.kind];
        return;
      }
      for (let c = 0; c < colCount; c += 1) {
        const addr = XLSX.utils.encode_cell({ r, c });
        let cell = ws[addr];
        if (!cell) { cell = { t: 's', v: '' }; ws[addr] = cell; }
        const isNum = c >= detailCount;
        if (meta.kind === 'header') {
          cell.s = STYLES.header;
        } else if (meta.kind === 'opening') {
          cell.s = isNum ? STYLES.openingNum : STYLES.openingText;
        } else if (meta.kind === 'total') {
          cell.s = isNum ? STYLES.totalNum : STYLES.totalText;
        } else if (meta.kind === 'stripe') {
          cell.s = isNum ? STYLES.stripeNum : STYLES.stripeText;
        } else {
          cell.s = isNum ? STYLES.dataNum : STYLES.dataText;
        }
        if (isNum && typeof cell.v === 'number') cell.z = AMOUNT_NUMBER_FORMAT;
      }
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Transactions');
    applyWorkbookRtl(wb);
    try {
      await translateWb(wb);
    } catch {
      return;
    }
    XLSX.writeFile(wb, `${data.clientName}_AccountCardReport.xlsx`);
  };

  const convertToPdf = async () => {
    try {
      const res = await EnumsService.GeneratePdfFromHtml(
        await translateHtml(htmlForAccountCardPdf(reportHtmlContent)),
        data.clientCase,
        token
      );
      if (!res.ok) {
        console.error("PDF error", res.status);
        return;
      }
      
      // Check response content type
      const contentType = res.headers.get('content-type');
      let blob;
      
      // Check if response is JSON or direct blob
      if (contentType && contentType.includes('application/json')) {
        // Response is JSON with base64
        const responseData = await res.json();
        const pdfBase64 = responseData.data?.PdfBase64 || responseData.data?.pdfBase64;
        
        if (!pdfBase64) {
          console.error('No PDF base64 in response');
          return;
        }
        
        // Convert base64 to blob
        const binaryString = atob(pdfBase64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        blob = new Blob([bytes], { type: 'application/pdf' });
      } else {
        // Response is direct blob (PDF)
        blob = await res.blob();
      }
      
      // Download the PDF
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${data.clientName}-${data.clientCase}_דוח כרטסת.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    }
  };

  const handlePrint = async () => {
    const win = window.open("", "_blank");
    let html;
    try {
      html = await translateHtml(reportHtmlContent);
    } catch {
      win.close();
      return;
    }
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => {
      win.print();
      // win.close();
    }, 400);
  };

  // Chat functionality - Ask if user wants to add signature
  const handleSendToChat = async () => {
    const result = await Swal.fire({
      title: t('AddSignatureTitle') || 'Add Signature?',
      text: t('AddSignatureQuestion') || 'Do you want to add a digital signature to this document before sending?',
      icon: 'question',
      showCancelButton: true,
      showCloseButton: true,     // Show X button
      confirmButtonColor: '#667eea',
      cancelButtonColor: '#6c757d',
      confirmButtonText: t('YesAddSignature') || 'Yes, Add Signature',
      cancelButtonText: t('NoSendWithout') || 'No, Send Without Signature',
      reverseButtons: true,
      allowOutsideClick: true,  // Allow closing by clicking outside
      allowEscapeKey: true,      // Allow closing with ESC key
      didOpen: () => {
        // Move close button to the left with !important
        const closeButton = document.querySelector('.swal2-close');
        if (closeButton) {
          closeButton.style.setProperty('right', 'auto', 'important');
          closeButton.style.setProperty('left', '10px', 'important');
        }
      }
    });

    if (result.isConfirmed) {
      // User clicked "Yes, Add Signature" - open signature modal
      setShowSignatureModal(true);
    } else if (result.dismiss === Swal.DismissReason.cancel) {
      // User clicked "No, Send Without Signature" button - send without signature
      await handleSendWithoutSignature();
    }
    // If user closes by clicking outside, ESC, or X button - do nothing (don't send)
  };

  // Helper function to generate HTML with signature (synchronous)
  const generateHtmlWithSignature = (signatureDataUrl) => {
    if (!data) return "";

    // Create signature section HTML
    const signatureSection = `
<div class="signature-section" style="margin-top: 40px; padding: 20px; page-break-inside: avoid;">
  <div style="border-top: 2px solid #333; padding-top: 20px;">
    <div style="display: flex; justify-content: space-between; align-items: flex-end; flex-wrap: wrap; gap: 20px;">
      <div style="flex: 1; min-width: 200px;">
        <div style="text-align: center; margin-bottom: 10px;">
          <img src="${signatureDataUrl}" alt="Signature" style="max-width: 300px; max-height: 100px; border: 1px solid #e0e0e0; padding: 5px; background: white;" />
        </div>
        <div style="border-top: 2px solid #000; padding-top: 5px; text-align: center;">
          <strong>חתימה מורשית</strong>
        </div>
      </div>
      <div style="flex: 1; min-width: 200px; text-align: center;">
        <div style="margin-bottom: 10px;">
          ${new Date().toLocaleDateString('he-IL', { year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
        <div style="border-top: 2px solid #000; padding-top: 5px;">
          <strong>תאריך</strong>
        </div>
      </div>
    </div>
  </div>
</div>`;

    // Get the current HTML content without signature
    let htmlWithoutSignature = reportHtmlContent;
    
    // Find the closing </div> before </body> and insert signature
    // Try multiple patterns to ensure we find the right place
    let htmlWithSignature = htmlWithoutSignature;
    
    // Pattern 1: Try to find </div>\n\n</body>
    if (htmlWithSignature.includes('</div>\n\n</body>')) {
      htmlWithSignature = htmlWithSignature.replace(
        '</div>\n\n</body>',
        `${signatureSection}\n</div>\n\n</body>`
      );
    }
    // Pattern 2: Try </div></body>
    else if (htmlWithSignature.includes('</div></body>')) {
      htmlWithSignature = htmlWithSignature.replace(
        '</div></body>',
        `${signatureSection}\n</div></body>`
      );
    }
    // Pattern 3: Just before </body>
    else {
      htmlWithSignature = htmlWithSignature.replace(
        '</body>',
        `${signatureSection}\n</body>`
      );
    }

    return htmlWithSignature;
  };

  // Handle sending without signature
  const handleSendWithoutSignature = async () => {
    try {
      // Show loading toast
      const loadingToast = toast.info(t('GeneratingPDF') || 'Generating PDF...', {
        autoClose: false,
        closeButton: false
      });

      try {
        // First, open chat with the client
        await selectUserAndOpenChat(data.clientCase, data.clientName || 'Client');

        // Generate PDF content WITHOUT signature
        const res = await EnumsService.GeneratePdfFromHtml(
          await translateHtml(htmlForAccountCardPdf(reportHtmlContent)),
          data.clientCase,
          token
        );
        if (!res.ok) {
          console.error("PDF generation error", res.status);
          const errorText = await res.text();
          console.error("Error details:", errorText);
          toast.dismiss(loadingToast);
          toast.error(t('ErrorGeneratingPDF'));
          return;
        }

        // Check response content type
        const contentType = res.headers.get('content-type');        
        let blob;
        
        // Check if response is JSON or direct blob
        if (contentType && contentType.includes('application/json')) {
          // Response is JSON with base64
          const responseData = await res.json();
          
          const pdfBase64 = responseData.data?.PdfBase64 || responseData.data?.pdfBase64;
          
          if (!pdfBase64) {
            toast.dismiss(loadingToast);
            toast.error(t('ErrorGeneratingPDF'));
            return;
          }
          
          // Convert base64 to blob
          const binaryString = atob(pdfBase64);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          blob = new Blob([bytes], { type: 'application/pdf' });
        } else {
          // Response is direct blob (PDF)
          blob = await res.blob();
        }
                 
        const file = new File([blob], `${data.clientName}-${data.clientCase}_דוח כרטסת.pdf`, { type: 'application/pdf' });

        // Send the file to chat
        await handleSendMessageToChat(file, data.clientCase, data.clientName);

        toast.dismiss(loadingToast);
        toast.success(t('DocumentSentToChat'));
        
      } catch (innerError) {
        console.error('Error in PDF generation/sending:', innerError);
        toast.dismiss(loadingToast);
        toast.error(t('ErrorSendingToChat'));
      }

    } catch (error) {
      console.error('Error sending document to chat:', error);
      toast.error(t('ErrorSendingToChat'));
    }
  };

  // Handle sending with signature after user signs
  const handleSendWithSignature = async (signature) => {
    try {
      setShowSignatureModal(false);

      // Show loading toast
      const loadingToast = toast.info(t('GeneratingSignedPDF') || 'Generating signed PDF...', {
        autoClose: false,
        closeButton: false
      });

      try {
        // First, open chat with the client
        await selectUserAndOpenChat(data.clientCase, data.clientName || 'Client');

        // Generate HTML with signature embedded SYNCHRONOUSLY
        const htmlWithSignature = generateHtmlWithSignature(signature);
        

        // Generate PDF content with signature embedded
        const res = await EnumsService.GeneratePdfFromHtml(
          await translateHtml(htmlForAccountCardPdf(htmlWithSignature)),
          data.clientCase,
          token
        );
        if (!res.ok) {
          console.error("PDF generation error", res.status);
          const errorText = await res.text();
          console.error("Error details:", errorText);
          toast.dismiss(loadingToast);
          toast.error(t('ErrorGeneratingPDF'));
          return;
        }
        

        // Check response content type
        const contentType = res.headers.get('content-type');        
        let blob;
        
        // Check if response is JSON or direct blob
        if (contentType && contentType.includes('application/json')) {
          // Response is JSON with base64
          const responseData = await res.json();
          
          const pdfBase64 = responseData.data?.PdfBase64 || responseData.data?.pdfBase64;
          
          if (!pdfBase64) {
            toast.dismiss(loadingToast);
            toast.error(t('ErrorGeneratingPDF'));
            return;
          }
          
          
          // Convert base64 to blob
          const binaryString = atob(pdfBase64);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          blob = new Blob([bytes], { type: 'application/pdf' });
        } else {
          // Response is direct blob (PDF)
          blob = await res.blob();
        }
                
        const file = new File([blob], `${data.clientName}-${data.clientCase}_דוח כרטסת.pdf`, { type: 'application/pdf' })

        // Send the file to chat
        await handleSendMessageToChat(file, data.clientCase, data.clientName);

        toast.dismiss(loadingToast);
        toast.success(t('DocumentSentToChat'));
        
      } catch (innerError) {
        console.error('Error in PDF generation/sending:', innerError);
        toast.dismiss(loadingToast);
        toast.error(t('ErrorSendingToChat'));
      }

    } catch (error) {
      console.error('Error sending document to chat:', error);
      toast.error(t('ErrorSendingToChat'));
    }
  };

  /* ------------ RENDER ------------ */
  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <Box sx={modalStyle} dir={direction}>
          <div
            className="pdf-view-upper"
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: '5px',
              flexWrap: 'wrap',
              gap: '8px',
              paddingTop: '24px',
            }}
          >
            <SleekButton
              onClick={convertToPdf}
              startIcon={<DownloadIcon />}
            >
              {t("DownloadPDFFile")}
            </SleekButton>

            <SleekButton
              onClick={downloadExcel}
              startIcon={<TableChartIcon />}
            >
              {t("DownloadExcelFile")}
            </SleekButton>

            <SleekButton
              onClick={handlePrint}
              startIcon={<PrintIcon />}
            >
              {t("PrintReport")}
            </SleekButton>

            {userTypeContext?.toString() !== Constants.UserType.Client &&
              <SleekButton
                onClick={handleSendToChat}
                startIcon={<ChatIcon />}
              >
                {t('SendToChat')}
              </SleekButton>
            }

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              backgroundColor: '#f8f9ff',
              borderRadius: '8px',
              border: '1px solid #e3e8ff',
              height: '36px',
            }}>
              <Checkbox
                checked={showAccountCodeId}
                onChange={(event) => setShowAccountCodeId(event.target.checked)}
                name="ShowAccountCodeId"
                color="primary"
                sx={{ padding: '2px' }}
                size="small"
              />
              <span style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>
                {t('ShowAccountCodeId')}
              </span>
            </div>

            <ReportLanguageSelector
              reportLanguage={reportLanguage}
              onReportLanguageChange={setReportLanguage}
              isTranslating={isTranslating}
            />
          </div>

          <div
            ref={reportContentRef}
            dir={previewDir}
            dangerouslySetInnerHTML={{ __html: displayHtml }}
          />
        </Box>
      </Modal>

      {/* Signature Modal - Rendered outside to avoid nested modal issues */}
      <SignatureModal
        open={showSignatureModal}
        handleClose={() => setShowSignatureModal(false)}
        onSendWithSignature={handleSendWithSignature}
        reportPreview={`${data?.clientName} - ${t('AccountCardReport') || 'Account Card Report'}`}
      />
    </>
  );
};

export default AccountCardReportHTML;
